<template>
	<div id="chart" ref="refChat" v-loading="quote_quoteKLine.loading"></div>
</template>

<script setup>
import { ref, defineExpose, getCurrentInstance, watch, nextTick } from 'vue';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	TitleComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import dayjs from 'dayjs';
import i18n, { EN, ZH } from '@/locales';
import { convertNumberToStr } from '@/utils/api';
import { useModuleData } from '@/rebuild/hooks/data/useModuleData';

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition,
	TitleComponent
]);

const props = defineProps({
	time: {
		type: Number,
		default: 1
	}
});

const { proxy } = getCurrentInstance();

const { isHKStock } = useStockVO();

const maxData = ref(0);

const upColor = i18n.locale === ZH ? '#fa4646' : '#30bf54';
const downColor = i18n.locale === ZH ? '#30bf54' : '#fa4646';
/**
 * @param {string} txt 文案
 * @param {string} value 值
 * @param {number} comParePrice 比较的值，判断颜色
 * @param {number} prePrice 前一天收盘价
 *
 */
function tooltipStr(txt, value, comParePrice, prePrice) {
	const color = comParePrice >= prePrice ? upColor : downColor;
	return `${txt}：<span style="color:${color}">${value}</span><br/>`;
}

// 正数补全+号
function addPlus(num) {
	return num > 0 ? '+' + num : num;
}

const formatEnUnit = amount => {
	return amount >= 1e9
		? (amount / 1e9).toFixed(2) + 'B'
		: amount >= 1e6
		? (amount / 1e6).toFixed(2) + 'M'
		: amount >= 1e3
		? (amount / 1e3).toFixed(2) + 'K'
		: amount.toFixed(2);
};

function splitData(rawData) {
	let categoryData = []; // [时间]
	let values = [];
	let volumes = [];
	for (let i = 0; i < rawData.length; i++) {
		const {
			tradingDay,
			avgPrice = 0,
			openPrice,
			closePrice,
			highPrice,
			lowPrice,
			changePct,
			changeQuota,
			turnoverValue,
			turnoverVolume,
			turnoverVolumeShares
		} = rawData[i];
		categoryData.push(dayjs(tradingDay).format('YYYY/MM/DD'));
		values.push({
			value: avgPrice,
			openPrice,
			closePrice,
			lowPrice,
			highPrice,
			changePct,
			changeQuota,
			turnoverVolume: isHKStock.value
				? turnoverVolumeShares
				: i18n.locale === EN
				? turnoverVolume * 100
				: turnoverVolume,
			turnoverValue
		});
		volumes.push([
			i,
			isHKStock.value ? turnoverVolumeShares : i18n.locale === EN ? turnoverVolume * 100 : turnoverVolume,
			openPrice > closePrice ? 1 : -1
		]);
	}
	return {
		categoryData: categoryData,
		values,
		volumes
	};
}

const { quote_quoteKLine } = useModuleData();

const queryQuote = async () => {
	await quote_quoteKLine.refresh({
		defaultTime: props.time
	});
	return quote_quoteKLine.data?.data?.reverse() || [];
};

let chatInstance = null;
const refChat = ref();
const refresh = async () => {
	if (chatInstance) {
		chatInstance.dispose();
		chatInstance.clear();
		chatInstance = null;
	}

	const data = await queryQuote();
	const { categoryData, values, volumes } = splitData(data);

	chatInstance = echarts.init(refChat.value);

	// 指定图表的配置项和数据
	const option = {
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'line',
				axis: 'x'
			},
			triggerOn: 'click',
			borderWidth: 1,
			borderColor: '#ccc',
			padding: 10,
			textStyle: {
				color: '#000'
			},
			position: function (pos, params, el, elRect, size) {
				const obj = {
					top: 10
				};
				obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
				return obj;
			},
			formatter: function (params) {
				let res = `<div style="color:#999;font-size:12px"><span style="color:#333">${params[0].name}</span> <br/>`;
				for (let i = 0, l = params.length; i < l; i++) {
					// 获取前一天收盘价
					const preCloseIndex = params[i].dataIndex > 0 ? params[i].dataIndex - 1 : 0;
					const preClose = values[preCloseIndex].closePrice;

					let { seriesName, data, seriesType } = params[i];

					if (seriesName === 'Volume') {
						res += '';
					} else if (seriesType === 'line') {
						res += tooltipStr(
							proxy.$t('enterprise.k-opening'),
							data['openPrice']?.toFixed(2),
							data['openPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-high'),
							data['highPrice']?.toFixed(2),
							data['highPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-low'),
							data['lowPrice']?.toFixed(2),
							data['lowPrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.k-closing'),
							data['closePrice']?.toFixed(2),
							data['closePrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.%Chg'),
							`${addPlus(data['changePct']?.toFixed(2))}%`,
							data['closePrice'],
							preClose
						);
						res += tooltipStr(
							proxy.$t('enterprise.chg'),
							data['changeQuota']?.toFixed(2),
							data['closePrice'],
							preClose
						);
						res +=
							`${proxy.$t('enterprise.k-volume')}：` +
							convertNumberToStr(data['turnoverVolume']) +
							`${i18n.locale === EN ? '' : isHKStock.value ? '股' : '手'}<br/>`;
						res +=
							`${proxy.$t('enterprise.k-turnover')}：` +
							convertNumberToStr(data['turnoverValue']) +
							'<br/>';
					} else {
						const val = typeof data === 'number' ? data?.toFixed(2) : '-';
						res += seriesName + '：' + val + '<br/>';
					}
				}
				return res + '</div>';
			}
			// extraCssText: 'width: 170px'
		},
		axisPointer: {
			link: [
				{
					xAxisIndex: 'all'
				}
			],
			label: {
				backgroundColor: '#777'
			}
		},
		visualMap: {
			show: false,
			seriesIndex: 1,
			dimension: 2,
			pieces: [
				{
					value: 1,
					color: downColor
				},
				{
					value: -1,
					color: upColor
				}
			]
		},
		grid: [
			{
				left: '2%',
				right: '2%',
				height: '62%',
				top: '3%',
				containLabel: true
			},
			{
				left: '2%',
				right: '2%',
				top: '73%',
				height: '25%',
				containLabel: true
			}
		],
		xAxis: [
			{
				type: 'category',
				data: categoryData,
				axisLine: {
					show: true,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				splitLine: {
					show: true,
					lineStyle: { color: '#F2F3F5', type: 'solid' }
				},
				axisTick: {
					show: false
				},
				axisLabel: {
					show: false
				}
			},
			{
				type: 'category',
				gridIndex: 1,
				data: categoryData,
				boundaryGap: true,
				axisLine: {
					onZero: false,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				axisLabel: {
					color: '#4E5969'
				},
				splitLine: {
					show: true,
					lineStyle: { color: '#F3F3F3', type: 'solid' }
				},
				min: 'dataMin',
				max: 'dataMax'
			}
		],
		yAxis: [
			{
				scale: true,
				type: 'value',
				axisLine: {
					show: true,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				axisLabel: {
					show: true,
					inside: true,
					color: '#4E5969'
				},
				splitNumber: 3,
				//interval: interval,
				splitLine: {
					show: true,
					lineStyle: { color: '#F2F3F5', type: 'solid' }
				}
				// min: minVal,
				// max: maxVal
			},
			{
				scale: true,
				gridIndex: 1,
				splitNumber: 2,
				axisLabel: {
					show: true,
					inside: true,
					color: '#4E5969',
					formatter(value, index) {
						if (index !== 0) {
							if (value < maxData.value) return; //移动端非最大值隐藏掉
							if (proxy.$i18n.locale === EN) return formatEnUnit(value);
							return value / 10000 + `${isHKStock.value ? '万股' : '万手'}`;
						} else {
							return '';
						}
					}
				},
				axisLine: {
					show: true,
					lineStyle: {
						color: '#E8EAEF'
					}
				},
				splitLine: {
					show: true,
					lineStyle: { color: '#F3F3F3', type: 'solid' }
				},
				axisTick: { show: false }
			}
		],
		dataZoom: [
			{
				type: 'inside',
				xAxisIndex: [0, 1],
				startValue: values.length - 60 - 1,
				endValue: values.length - 1
			}
		],
		series: [
			{
				type: 'line',
				xAxisIndex: 0,
				yAxisIndex: 0,
				itemStyle: {
					normal: {
						color: '#0882FD',
						opacity: 0
					},
					emphasis: {
						opacity: 1
					}
				},
				areaStyle: {
					color: `rgba(84,147,255,0.08)`
				},
				data: values,
				triggerLineEvent: true
			},
			{
				name: 'Volume',
				type: 'bar',
				data: volumes,
				xAxisIndex: 1,
				yAxisIndex: 1,
				barMaxWidth: 20
			}
		]
	};

	intMaxData(values);

	// 使用刚指定的配置项和数据显示图表。
	chatInstance.setOption(option);

	chatInstance.on('mousemove', function () {
		chatInstance.dispatchAction({
			type: 'hideTip'
		});
	});

	chatInstance.on('datazoom', params => {
		//需要在dataZoom范围内找出最大的交易量，以供y轴label判断
		const { batch } = params;
		const start = Math.floor((batch[0].start / 100) * values.length);
		const end = Math.ceil((batch[0].end / 100) * values.length);
		maxData.value = Math.max(...values.slice(start, end).map(item => item.turnoverVolume));
	});
};

const intMaxData = data => {
	let start = data.length - 60 - 1;
	let end = data.length - 1;
	maxData.value = Math.max(...data.slice(start, end).map(item => item.turnoverVolume));
};

watch(
	[() => props.time, () => proxy.$i18n.locale],
	() => {
		nextTick(() => {
			refresh();
		});
	},
	{ immediate: true }
);

defineExpose({
	refresh
});
</script>

<style lang="less" scoped>
#chart {
	width: 100%;
	height: 368px;
}
</style>
